<template>
  <div class="product-traits" v-loading="loading">
    <template v-if="schema">
      <el-select ref="typeSelect" :value="productTypes" @input="setProductTypes" multiple filterable
        class="stretched" placeholder="Tipos do produto">
        <el-option-group v-for="category in schema.categories" :key="category.trait"
          :label="label(category.trait)">
          <el-option v-for="type in category.productTypes" :key="type" :value="type"
            :label="label(type)">
          </el-option>
        </el-option-group>
      </el-select>

      <!-- Saved with the product but never asked, so they're listed instead of added unseen -->
      <div v-if="categories.length > 0" class="trait-question trait-derived">
        <div class="trait-question-header">
          <span class="trait-question-label">Definidos pelo tipo</span>
        </div>
        <div class="trait-question-hint">
          Salvos automaticamente a partir dos tipos escolhidos e atualizados quando eles mudam.
        </div>
        <div class="trait-derived-row">
          <span class="trait-derived-label">Categoria:</span>
          <el-tag v-for="trait in categories" :key="trait" size="small" type="info"
            disable-transitions>{{ label(trait) }}</el-tag>
        </div>
      </div>

      <div v-for="question in questions" :key="question.group.key" class="trait-question">
        <div class="trait-question-header">
          <span class="trait-question-label">{{ groupLabel(question.group) }}</span>
          <el-tag v-if="question.whenMissing === 'ERROR'" size="mini" type="danger"
            disable-transitions>Obrigatório</el-tag>
          <el-tag v-else-if="question.whenMissing === 'WARNING'" size="mini" type="warning"
            disable-transitions>Recomendado</el-tag>
          <el-tag v-if="suitsEveryone(question)" size="mini" type="info"
            disable-transitions>Serve para todos</el-tag>
          <!-- Where an answer is required, blank means unanswered, so "everyone" needs a click -->
          <el-button v-if="question.group.kind === 'SUITABILITY' && question.whenMissing"
            type="text" size="mini"
            @click="setAnswer(question, question.options)">Todos</el-button>
        </div>
        <div class="trait-question-hint">{{ hint(question) }}</div>
        <el-checkbox-group :value="selectedIn(question)"
          @input="(selected) => setAnswer(question, selected)">
          <el-checkbox v-for="trait in question.options" :key="trait" :label="trait">
            {{ label(trait) }}
          </el-checkbox>
        </el-checkbox-group>
      </div>

      <div v-if="strayTraits.length > 0" class="trait-question">
        <div class="trait-question-header">
          <span class="trait-question-label">{{ strayLabel }}</span>
        </div>
        <el-tag v-for="trait in strayTraits" :key="trait" type="danger" closable disable-transitions
          @close="remove(trait)">{{ labelWithGroup(trait) }}</el-tag>
      </div>

      <div v-for="problem in problems" :key="problem.key"
        :class="['trait-problem', problem.level.toLowerCase()]">
        <i :class="problem.level === 'ERROR' ? 'el-icon-error' : 'el-icon-warning'"></i>
        {{ problem.message }}
      </div>
      <div v-if="!schema.blocksSave && problems.some(problem => problem.level === 'ERROR')"
        class="trait-trial-note">
        Formulário em teste: por enquanto, os erros pedem confirmação, mas não impedem salvar.
      </div>
    </template>
  </div>
</template>
<script>
import TraitService from '@/services/stock/TraitService';
import {
  answerNeededHints, groupHints, groupLabels, kindHints, traitLabels,
} from './traitLabels';

// Strongest first, as in the API
const LEVELS = ['ERROR', 'WARNING'];

const strongest = (level, other) => LEVELS.find(it => it === level || it === other) || null;

const unique = list => [...new Set(list)];

/**
 * Edits a product's traits by asking, for its product types, the questions the API checks on save.
 * Emits `problems` with the errors and warnings found, and whether the API refuses to save errors.
 */
export default {
  name: 'product-traits-form',
  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      schema: null,
      loading: true,
    };
  },
  async created() {
    try {
      this.schema = await TraitService.schema();
    } catch (error) {
      this.$message.error(`Não foi possível carregar os traits: ${error.message}`);
    } finally {
      this.loading = false;
    }
  },
  computed: {
    categoryTraits() {
      return this.schema.categories.map(category => category.trait);
    },
    productTypeSet() {
      return new Set(this.schema.categories.flatMap(category => category.productTypes));
    },
    groupByTrait() {
      return Object.fromEntries(
        this.schema.groups.flatMap(group => group.traits.map(trait => [trait, group])),
      );
    },
    // The category traits follow the product types, so they never need an answer or a removal
    traits() {
      return this.value.filter(trait => !this.categoryTraits.includes(trait));
    },
    productTypes() {
      return this.traits.filter(trait => this.productTypeSet.has(trait));
    },
    categories() {
      return this.categoriesOf(this.productTypes);
    },
    // Over all the product types: the strongest level and every option any of them offers
    questions() {
      const levels = {};
      const offered = new Set();

      this.productTypes.forEach((type) => {
        (this.schema.questions[type] || []).forEach(({ group, whenMissing, options }) => {
          levels[group] = strongest(levels[group], whenMissing);
          options.forEach(option => offered.add(option));
        });
      });

      return this.schema.groups
        .filter(group => group.key in levels)
        .map(group => ({
          group,
          whenMissing: levels[group.key],
          options: group.traits.filter(trait => offered.has(trait)),
        }));
    },
    unsupportedTraits() {
      return unique(
        this.traits.filter(trait => !this.productTypeSet.has(trait) && !this.groupByTrait[trait]),
      );
    },
    notApplicableTraits() {
      const allowed = new Set(this.questions.flatMap(question => question.options));

      return unique(this.traits.filter(trait => this.groupByTrait[trait] && !allowed.has(trait)));
    },
    // Traits no question shows, listed so they can be removed; without a product type, that's all
    strayTraits() {
      return this.productTypes.length > 0
        ? [...this.unsupportedTraits, ...this.notApplicableTraits]
        : unique(this.traits);
    },
    strayLabel() {
      if (this.productTypes.length === 0) return 'Sem tipo de produto';

      return 'Não se aplicam a este produto';
    },
    problems() {
      if (!this.schema) return [];

      if (this.traits.length === 0) {
        const message = 'Sem traits, o produto pode ir para qualquer assinante.';

        return [this.problem('WARNING', 'NO_TRAITS', message)];
      }

      const unsupported = this.unsupportedTraits
        .map(trait => this.problem('ERROR', trait, `"${this.label(trait)}" não é mais usado.`));

      if (this.productTypes.length === 0) {
        const message = 'Escolha ao menos um tipo de produto.';

        return [...unsupported, this.problem('ERROR', 'NO_PRODUCT_TYPE', message)];
      }

      const notApplicable = this.notApplicableTraits.map(trait => this.problem(
        'ERROR', trait, `"${this.labelWithGroup(trait)}" não se aplica aos tipos escolhidos.`,
      ));
      const unanswered = this.questions
        .filter(question => question.whenMissing && this.selectedIn(question).length === 0)
        .map(({ group, whenMissing }) => this.problem(
          whenMissing,
          group.key,
          whenMissing === 'ERROR'
            ? `Preencha "${this.groupLabel(group)}": é obrigatório para estes tipos.`
            : `"${this.groupLabel(group)}" não foi preenchido.`,
        ));

      return [...unsupported, ...notApplicable, ...unanswered];
    },
  },
  watch: {
    problems: {
      handler(problems) {
        this.$emit('problems', problems, Boolean(this.schema && this.schema.blocksSave));
      },
      immediate: true,
    },
  },
  methods: {
    problem(level, key, message) {
      return { level, key, message };
    },
    label(trait) {
      return traitLabels[trait] || trait;
    },
    // For a trait shown outside its question, where "Ressecamento" could be skin or hair
    labelWithGroup(trait) {
      const group = this.groupByTrait[trait];

      return group ? `${this.groupLabel(group)}: ${this.label(trait)}` : this.label(trait);
    },
    groupLabel(group) {
      return groupLabels[group.key] || group.key;
    },
    // Hints offer leaving a question blank only where that is an answer: not where it blocks
    // saving, nor on suitability questions that warn, where "everyone" is answered with "Todos"
    hint({ group, whenMissing }) {
      const needsAnswer = whenMissing === 'ERROR' || (whenMissing && group.kind === 'SUITABILITY');

      return groupHints[group.key]
        || (needsAnswer && answerNeededHints[group.kind])
        || kindHints[group.kind];
    },
    selectedIn(question) {
      return this.traits.filter(trait => question.options.includes(trait));
    },
    // Product selection reads blank the same as every option ticked
    suitsEveryone(question) {
      if (question.group.kind !== 'SUITABILITY') return false;

      const selected = this.selectedIn(question);

      if (selected.length === 0) return !question.whenMissing;

      return question.options.every(trait => selected.includes(trait));
    },
    setAnswer(question, selected) {
      const others = this.traits.filter(trait => !question.options.includes(trait));

      this.emitTraits([...others, ...selected]);
    },
    // A chosen type ticks its usual exclusion families and a removed one unticks those no other
    // type has; either way they can still be changed by hand
    setProductTypes(types) {
      const added = types.filter(type => !this.productTypes.includes(type));
      const removed = this.productTypes.filter(type => !types.includes(type));
      const kept = this.defaultsFor(types);
      const unticked = this.defaultsFor(removed).filter(trait => !kept.includes(trait));
      const others = this.traits
        .filter(trait => !this.productTypeSet.has(trait) && !unticked.includes(trait));

      this.emitTraits([...types, ...others, ...this.defaultsFor(added)]);

      // Most products have a single type, so the menu closes after a pick and a click reopens
      // it. On the next tick, since the select reopens it itself right after the pick
      if (added.length > 0) this.$nextTick(() => this.$refs.typeSelect.blur());
    },
    remove(trait) {
      this.emitTraits(this.traits.filter(it => it !== trait));
    },
    defaultsFor(productTypes) {
      return unique(productTypes.flatMap(type => this.schema.defaultTraits[type] || []));
    },
    categoriesOf(productTypes) {
      return this.schema.categories
        .filter(category => productTypes.some(type => category.productTypes.includes(type)))
        .map(category => category.trait);
    },
    // Same as the API saves them: the categories the product types imply first, no repeats
    emitTraits(traits) {
      const others = unique(traits.filter(trait => !this.categoryTraits.includes(trait)));
      const types = others.filter(trait => this.productTypeSet.has(trait));

      this.$emit('input', [...this.categoriesOf(types), ...others]);
    },
  },
};
</script>

<style lang="scss" scoped>
div.product-traits {
  text-align: left;
  line-height: normal;
}

div.trait-question {
  border: #c1c1c1 dashed 1.5px;
  border-radius: 4px;
  margin-top: 8px;
  padding: 8px 12px;

  .el-tag {
    margin: 4px;
  }

  // Options in equal columns, so a wrapped row lines up with the one above, and every question
  // with the others
  .el-checkbox-group {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    row-gap: 6px;
  }

  .el-checkbox {
    margin-right: 0;
  }
}

div.trait-question-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

span.trait-question-label {
  font-weight: 600;
  color: #2c3e50;
}

div.trait-question-hint {
  margin: 4px 0 8px;
  font-size: 12px;
  color: #5e7382;
  white-space: pre-line;
}

div.trait-derived-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 4px;

  .el-tag {
    margin: 0;
  }
}

span.trait-derived-label {
  font-size: 13px;
  color: #2c3e50;
}

div.trait-trial-note {
  margin-top: 6px;
  font-size: 12px;
  color: #5e7382;
}

div.trait-problem {
  margin-top: 6px;
  font-size: 13px;

  &.error {
    color: #f56c6c;
  }

  &.warning {
    color: #e6a23c;
  }
}
</style>
